import React from "react";

/**
 * Two-handle range slider.
 *
 * Built from two overlapping native range inputs rather than a library. The
 * inputs themselves are transparent and non-interactive; only their thumbs
 * take pointer events, and the visible track and fill are drawn underneath.
 * That keeps native keyboard and accessibility behaviour, which a div-and-
 * pointer-events implementation loses.
 *
 * The thumb styling has to live in a stylesheet because ::-webkit-slider-thumb
 * cannot be expressed as an inline style.
 */

const THUMB = 24;

const css = `
.lu-range { position: relative; height: ${THUMB}px; }
.lu-range__track,
.lu-range__fill {
  position: absolute; top: 50%; transform: translateY(-50%);
  height: 6px; border-radius: 999px; pointer-events: none;
}
.lu-range__track { left: 0; right: 0; background: #E5E7EB; }
.lu-range__fill  { background: #2563EB; }

.lu-range input[type="range"] {
  position: absolute; top: 0; left: 0;
  width: 100%; height: ${THUMB}px; margin: 0;
  background: none; appearance: none; -webkit-appearance: none;
  pointer-events: none;
}
.lu-range input[type="range"]:focus { outline: none; }

.lu-range input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none;
  width: ${THUMB}px; height: ${THUMB}px;
  border-radius: 50%; background: #FFFFFF;
  border: 3px solid #2563EB; box-sizing: border-box;
  cursor: grab; pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.lu-range input[type="range"]::-webkit-slider-thumb:active { cursor: grabbing; }

.lu-range input[type="range"]::-moz-range-thumb {
  width: ${THUMB}px; height: ${THUMB}px;
  border-radius: 50%; background: #FFFFFF;
  border: 3px solid #2563EB; box-sizing: border-box;
  cursor: grab; pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.lu-range input[type="range"]::-moz-range-track { background: none; }

.lu-range input[type="range"]:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 4px rgba(37,99,235,0.25);
}
`;

let injected = false;
function useRangeStyles() {
  if (typeof document === "undefined" || injected) return;
  const el = document.createElement("style");
  el.setAttribute("data-lu-range", "");
  el.textContent = css;
  document.head.appendChild(el);
  injected = true;
}

export default function RangeSlider({ min, max, step, value, onChange, label }) {
  useRangeStyles();

  const [lo, hi] = value;
  const span = max - min || 1;
  const loPct = ((lo - min) / span) * 100;
  const hiPct = ((hi - min) / span) * 100;

  // Clamped so the handles cannot cross. Each stops one step short of the
  // other rather than stacking, which would make the far handle ungrabbable.
  const setLo = (next) => onChange([Math.min(next, hi - step), hi]);
  const setHi = (next) => onChange([lo, Math.max(next, lo + step)]);

  return (
    <div className="lu-range">
      <div className="lu-range__track" />
      <div
        className="lu-range__fill"
        style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={lo}
        aria-label={`${label} minimum`}
        onChange={(e) => setLo(Number(e.target.value))}
        // Raised when both handles sit at the top of the track, so the lower
        // handle stays grabbable instead of being buried under the upper one
        style={{ zIndex: lo > max - (max - min) / 10 ? 4 : 3 }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={hi}
        aria-label={`${label} maximum`}
        onChange={(e) => setHi(Number(e.target.value))}
        style={{ zIndex: 3 }}
      />
    </div>
  );
}
